import { AfterContentInit, Component, contentChildren, inject, input, model, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { PrivaHeaderComponent } from '@priva/components/header';
import { PrivaWellModule } from '@priva/components/well';
import { PrivaWizardIndicatorModule } from '@priva/components/wizard/indicator';
import { PrivaWizardToolbarButton, PrivaWizardToolbarModule } from '@priva/components/wizard/toolbar';

import { resetApiConfig } from 'app/state/api-config/api-config.actions';

import { AppWizardStep } from './app-wizard.step';
import { wizardToolbarButtons } from './constants/wizard-toolbar-actions';

@Component({
    selector: 'app-wizard',
    templateUrl: './app-wizard.component.html',
    styleUrl: './app-wizard.component.scss',
    imports: [PrivaHeaderComponent, PrivaWizardIndicatorModule, PrivaWizardToolbarModule, PrivaWellModule],
})
export class AppWizardComponent implements AfterContentInit {
    private store = inject(Store);

    buttons = model<PrivaWizardToolbarButton[]>(wizardToolbarButtons);
    title = input('');

    completed = output<void>();

    readonly wizardSteps = contentChildren<AppWizardStep>('step');

    private unsubscribe$ = new Subject<void>();
    private internalSteps: AppWizardStep[] | undefined;
    ready = false;
    stepIndex = 0;
    breakpoint = 10;

    get steps(): AppWizardStep[] {
        if (!this.internalSteps) {
            this.internalSteps = this.wizardSteps().map((s) => s);
        }

        return this.internalSteps;
    }

    set steps(value: AppWizardStep[]) {
        this.internalSteps = value;
    }

    onIndicatorStepClick($event: number) {
        this.stepIndex = $event;
        this.updateButtonStates();
    }

    onToolbarButtonClick(button: PrivaWizardToolbarButton) {
        this.handleButtonClick(button.id);
    }

    private getButton(id: string): PrivaWizardToolbarButton {
        const privaWizardToolbarButton = this.buttons().find((button) => button.id === id);

        if (!privaWizardToolbarButton) {
            throw new Error('Toolbar button not found!!');
        }

        return privaWizardToolbarButton;
    }

    private handleButtonClick(buttonId: string): void {
        switch (buttonId) {
            case 'reset':
                this.store.dispatch(resetApiConfig());
                this.stepIndex = 0;
                this.steps = [
                    ...this.steps.map((step) => {
                        step.completed = false;
                        return step;
                    }),
                ];
                break;
            case 'prev':
                if (this.stepIndex > 0) {
                    this.stepIndex--;
                    this.steps[this.stepIndex].completed = false;
                    this.steps = [...this.steps];
                }
                break;
            case 'next':
                if (this.steps.length > this.stepIndex + 1) {
                    this.steps[this.stepIndex].completed = true;
                    this.stepIndex++;
                    this.steps = [...this.steps];
                }
                break;
            case 'submit':
                this.completed.emit();
                break;
        }

        this.updateButtonStates();
        this.buttons.set([...this.buttons()]);

        this.showScreen(this.stepIndex);
    }

    private updateButtonStates(): void {
        const screen = this.getScreenByIndex(this.stepIndex);

        this.getButton('prev').hidden = this.stepIndex === 0;
        this.getButton('next').hidden = this.stepIndex === this.steps.length - 1 || !screen.canGoNext$.value;
        this.getButton('submit').hidden =
            !(this.stepIndex === this.steps.length - 1) || !screen.canGoNext$.value;

        this.forceButtonUpdate();
    }

    onBreakpointChange(isMobile: boolean): void {
        this.getButton('prev').iconState = isMobile ? 'Only' : 'Left';
        this.getButton('prev').text = isMobile ? '' : 'Previous';
        this.forceButtonUpdate();
    }

    private showScreen(index: number): void {
        this.hideAllScreens();
        const screen = this.getScreenByIndex(index);
        screen.show();
        screen.next$.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => this.handleButtonClick('next'));
        screen.canGoNext$.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
            this.updateButtonStates();
        });
    }

    private hideAllScreens(): void {
        this.unsubscribe$.next();
        this.steps.forEach((s) => s.hide());
    }

    private getScreenByIndex(index: number): AppWizardStep {
        return this.steps[index];
    }

    ngAfterContentInit(): void {
        this.ready = true;
        this.showScreen(this.stepIndex);
    }

    private forceButtonUpdate(): void {
        this.buttons.set([...this.buttons()]);
    }
}
